import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Phone, Loader2, AlertCircle, Home, MessageCircle } from 'lucide-react';
import { companyInfo } from '../data/mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId, 0);
    } else {
      setError('No session ID found');
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sessionId, attempts) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setError('Payment verification timed out. Please contact us if payment was taken.');
      setStatus('error');
      return;
    }

    try {
      const response = await fetch(`${API}/bookings/payment-status/${sessionId}`);
      if (!response.ok) {
        throw new Error('Failed to check payment status');
      }

      const data = await response.json();

      if (data.payment_status === 'paid') {
        setStatus('success');
        setBooking(data.booking);
        return;
      } else if (data.status === 'expired') {
        setError('Payment session expired. Please try again.');
        setStatus('error');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (err) {
      console.error('Error checking payment status:', err);
      setError('Error checking payment status. Please contact us.');
      setStatus('error');
    }
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <Loader2 className="w-16 h-16 text-construction-yellow animate-spin mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-construction-darkblue mb-2">Verifying Payment...</h1>
          <p className="text-gray-600">Please wait while we confirm your payment.</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-10 h-10 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-construction-darkblue mb-2">Payment Issue</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="space-y-3">
            <a
              href={`tel:${companyInfo.phone}`}
              className="block w-full bg-construction-yellow text-construction-darkblue px-6 py-3 rounded-lg font-bold hover:bg-yellow-500 transition-all"
            >
              <Phone className="inline mr-2" size={20} />
              Call Us: {companyInfo.phone}
            </a>
            <Link
              to="/book-test"
              className="block w-full bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-bold hover:bg-gray-300 transition-all"
            >
              Try Again
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Success Card */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-green-600 text-white py-8 px-6 text-center">
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold mb-2">Booking Confirmed!</h1>
            <p className="text-green-100">Your payment has been received successfully</p>
          </div>

          {/* Booking Details */}
          <div className="p-6 md:p-8">
            <div className="bg-gray-50 rounded-xl p-6 mb-6">
              <h2 className="text-xl font-bold text-construction-darkblue mb-4">Booking Details</h2>
              
              {booking && (
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">Reference:</span>
                    <span className="font-mono font-bold text-sm">{booking.id?.slice(0, 8).toUpperCase()}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">Name:</span>
                    <span className="font-medium">{booking.firstName} {booking.lastName}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">Test Type:</span>
                    <span className="font-medium">{booking.testTypeName}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">Email:</span>
                    <span className="font-medium">{booking.email}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600">Amount Paid:</span>
                    <span className="font-bold text-green-600 text-lg">£{booking.price}</span>
                  </div>
                </div>
              )}
            </div>

            {/* What's Next */}
            <div className="bg-construction-yellow/10 border-l-4 border-construction-yellow p-6 rounded-r-lg mb-6">
              <h3 className="font-bold text-construction-darkblue mb-3">What Happens Next?</h3>
              <ol className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="bg-construction-yellow text-construction-darkblue w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
                  <span>You will receive a confirmation email shortly</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-construction-yellow text-construction-darkblue w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
                  <span>Our team will contact you within 24 hours to arrange your test date</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-construction-yellow text-construction-darkblue w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
                  <span>You'll receive test centre details and preparation materials</span>
                </li>
              </ol>
            </div>

            {/* Contact */}
            <div className="text-center">
              <p className="text-gray-600 mb-4">Have questions about your booking?</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="inline-flex items-center justify-center gap-2 bg-construction-darkblue text-white px-6 py-3 rounded-lg font-bold hover:bg-black transition-all"
                >
                  <Phone size={20} />
                  {companyInfo.phone}
                </a>
                <a
                  href={`https://wa.me/${companyInfo.whatsapp.replace(/\+/g, '')}?text=Hi, I just booked a test (Ref: ${booking?.id?.slice(0, 8).toUpperCase()})`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-green-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-green-700 transition-all"
                >
                  <MessageCircle size={20} />
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Return Home */}
        <div className="text-center mt-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-construction-darkblue font-medium hover:text-construction-yellow transition-colors"
          >
            <Home size={20} />
            Return to Homepage
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BookingSuccessPage;
